import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import { storedDistrictMatchesDirectoryKey } from "../data/geoDirectoryStore";
import { normalizeGeoLabel } from "./geoLabel";
import { toDisplayCamelCase } from "./textCase";

export type GeoSelectOption = { value: string; label: string; rowKey: string };

export function districtSelectOptionsFromStore(store: GeoDirectoryStore | null | undefined): GeoSelectOption[] {
  if (!store) return [];
  return [...store.tgRegistrationDistricts()]
    .sort((a, b) =>
      a.registrationDistrictName.localeCompare(b.registrationDistrictName, undefined, { sensitivity: "base" }),
    )
    .map((d) => ({
      value: d.districtKey,
      label: toDisplayCamelCase(d.registrationDistrictName),
      rowKey: d.registrationDistrictCode,
    }));
}

export function mandalSelectOptionsFromStore(
  store: GeoDirectoryStore | null | undefined,
  districtKey: string,
): GeoSelectOption[] {
  if (!store || !districtKey) return [];
  return [...store.tgMandalsForDistrict(districtKey)]
    .sort((a, b) => a.mandalName.localeCompare(b.mandalName, undefined, { sensitivity: "base" }))
    .map((m) => ({
      value: m.mandalName,
      label: toDisplayCamelCase(m.mandalName),
      rowKey: m.mandalCode,
    }));
}

export function villageSelectOptionsFromStore(
  store: GeoDirectoryStore | null | undefined,
  districtKey: string,
  mandalName: string,
  embedVillageDbIdInValue = false,
): GeoSelectOption[] {
  if (!store || !districtKey || !mandalName) return [];
  return [...store.tgVillagesForMandal(districtKey, mandalName)]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .map((v) => {
      const useId = embedVillageDbIdInValue && typeof v.id === "number" && v.id > 0;
      const value = useId ? `${v.id}\t${v.name}` : v.name;
      return { value, label: toDisplayCamelCase(v.name), rowKey: `v-${v.id ?? v.code}` };
    });
}

export function geoLocationPassesFilter(
  geo: GeoDirectoryStore | null | undefined,
  stored: { district?: string | null; mandal?: string | null; village?: string | null },
  filter: { district?: string; mandal?: string; village?: string },
): boolean {
  const { district, mandal, village } = filter;
  if (district) {
    if (geo) {
      if (!storedDistrictMatchesDirectoryKey(geo, district, stored.district)) return false;
    } else if (normalizeGeoLabel(stored.district ?? "") !== normalizeGeoLabel(district)) {
      return false;
    }
  }
  if (mandal && normalizeGeoLabel(stored.mandal ?? "") !== normalizeGeoLabel(mandal)) return false;
  if (village && normalizeGeoLabel(stored.village ?? "") !== normalizeGeoLabel(village)) return false;
  return true;
}

/** Label for a district key/name using the geo directory when available. */
export function districtDisplayLabel(
  geo: GeoDirectoryStore | null | undefined,
  district: string | null | undefined,
): string {
  const raw = String(district ?? "").trim();
  if (!raw) return "—";
  const row = geo?.findDistrict(raw);
  if (row) return toDisplayCamelCase(row.registrationDistrictName);
  return toDisplayCamelCase(raw);
}
